import { useState } from "react";
import { X, Plus, Save } from "lucide-react";

export const inputCls =
  "w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm focus:outline-none focus:border-ember transition-colors";

export function Section({ id, title, subtitle, children, onSave, saving, testId }) {
  return (
    <section id={id} className="bg-surface rounded-card border border-hairline p-6 md:p-8 scroll-mt-24" data-testid={testId}>
      <div className="mb-6">
        <h2 className="font-display font-medium text-xl md:text-2xl tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-sand-500 mt-1">{subtitle}</p>}
      </div>
      <div className="space-y-5">{children}</div>
      <div className="mt-8 pt-5 border-t border-sand-100 flex justify-end">
        <button onClick={onSave} disabled={saving} className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid={`${testId}-save`}>
          <Save size={15} className="mr-2" /> {saving ? "Saving…" : "Save section"}
        </button>
      </div>
    </section>
  );
}

export function Row({ cols = 2, children }) {
  const cls = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" }[cols] || "md:grid-cols-2";
  return <div className={`grid grid-cols-1 ${cls} gap-5`}>{children}</div>;
}

export function TextField({ label, value, onChange, placeholder, mono, testId }) {
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <input className={`${inputCls} ${mono ? "font-mono" : ""}`} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

export function NumField({ label, value, onChange, placeholder, min, max, step, testId }) {
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <input type="number" className={`${inputCls} font-mono`} value={value ?? ""} placeholder={placeholder}
        min={min} max={max} step={step}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testId} />
    </div>
  );
}

export function TextAreaField({ label, value, onChange, placeholder, rows = 3, testId }) {
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <textarea className={inputCls} rows={rows} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <select className={`${inputCls} font-mono`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function ToggleField({ label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-ember w-4 h-4" data-testid={testId} />
      <span>
        <span className="block text-sm font-medium text-sand-900">{label}</span>
        {hint && <span className="block text-xs text-sand-500 mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

export function ChipsField({ label, values, onChange, placeholder, hint, uppercase, testId }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    let v = draft.trim();
    if (uppercase) v = v.toUpperCase();
    if (v && !(values || []).includes(v)) onChange([...(values || []), v]);
    setDraft("");
  };
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testId}-chips`}>
        {(values || []).map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 bg-sand-100 border border-sand-200 px-2.5 py-1 text-xs font-mono">
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-sand-400 hover:text-sand-900" data-testid={`${testId}-remove-${v}`}>
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input className={`${inputCls} ${uppercase ? "font-mono uppercase" : ""}`} value={draft} placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          data-testid={testId} />
        <button type="button" onClick={add} className="btn-swiss btn-outline text-sm shrink-0" data-testid={`${testId}-add`}>
          <Plus size={14} />
        </button>
      </div>
      {hint && <p className="text-xs text-sand-400 mt-1.5">{hint}</p>}
    </div>
  );
}

export function SliderField({ label, leftLabel, rightLabel, value, onChange, testId }) {
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <input type="range" min={0} max={100} value={value ?? 50} onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-ember" data-testid={testId} />
      <div className="flex justify-between text-xs text-sand-500 mt-1">
        <span>{leftLabel}</span>
        <span className="font-mono font-bold text-ember">{value ?? 50}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}

export function PairRows({ label, rows, onChange, fields, addLabel, hint, testId }) {
  const update = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <div className="space-y-2">
        {(rows || []).map((row, i) => (
          <div key={i} className="flex flex-wrap gap-2 items-start" data-testid={`${testId}-row-${i}`}>
            {fields.map((f) => (
              f.type === "toggle" ? (
                <label key={f.key} className="flex items-center gap-1.5 px-2 py-2.5 text-xs whitespace-nowrap cursor-pointer">
                  <input type="checkbox" checked={!!row[f.key]} onChange={(e) => update(i, f.key, e.target.checked)} className="accent-ember" />
                  {f.placeholder}
                </label>
              ) : (
                <input key={f.key} className={`border border-sand-300 bg-surface px-3 py-2.5 text-sm focus:outline-none focus:border-ember transition-colors min-w-0 ${f.mono ? "font-mono uppercase" : ""} ${f.width ? `${f.width} shrink-0` : "flex-1"}`}
                  value={row[f.key] ?? ""} placeholder={f.placeholder} data-testid={`${testId}-row-${i}-${f.key}`}
                  onChange={(e) => update(i, f.key, f.mono ? e.target.value.toUpperCase() : e.target.value)} />
              )
            ))}
            <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
              className="p-2.5 text-sand-400 hover:text-red-600" data-testid={`${testId}-remove-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="btn-swiss btn-outline text-xs mt-2"
        onClick={() => onChange([...(rows || []), Object.fromEntries(fields.map((f) => [f.key, f.type === "toggle" ? true : ""]))])}
        data-testid={`${testId}-add`}>
        <Plus size={12} className="mr-1.5" /> {addLabel}
      </button>
      {hint && <p className="text-xs text-sand-400 mt-1.5">{hint}</p>}
    </div>
  );
}
